import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, RefreshCw, Phone, MapPin } from "lucide-react";
import { adminApi, clearToken, getToken, formatApiErrorDetail } from "@/admin/api";

const STATUS = ["new", "contacted", "quoted", "closed"];
const statusColor = {
  new: "bg-primary text-white",
  contacted: "bg-signal-yellow text-black",
  quoted: "bg-black text-[#F5F4EF] border border-white/30",
  closed: "bg-white/10 text-[#F5F4EF]/60",
};

export default function AdminDashboard() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/enquiries");
      setItems(data);
    } catch (err) {
      if (err.response?.status === 401) {
        clearToken();
        navigate("/admin/login");
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/enquiries/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? data : it)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await adminApi.delete(`/enquiries/${id}`);
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const logout = () => {
    clearToken();
    navigate("/admin/login");
  };

  const shown = filter === "all" ? items : items.filter((i) => i.status === filter);
  const counts = STATUS.reduce((a, s) => ({ ...a, [s]: items.filter((i) => i.status === s).length }), {});

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#F5F4EF] text-black">
      <header className="bg-black text-[#F5F4EF] sticky top-0 z-10">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="h-8 w-8 bg-primary flex items-center justify-center font-mono-accent text-sm text-white">R</span>
            <span className="font-display text-lg tracking-tight">RICH GLOBAL <span className="text-primary">RMC</span> <span className="text-[#F5F4EF]/40">/ Enquiries</span></span>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh-btn" onClick={load} className="flex items-center gap-2 border border-white/25 px-4 py-2 font-mono-accent text-xs uppercase tracking-[0.2em] hover:border-primary hover:text-primary transition-colors">
              <RefreshCw size={14} /> Refresh
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="flex items-center gap-2 bg-primary text-white px-4 py-2 font-mono-accent text-xs uppercase tracking-[0.2em] hover:bg-signal-yellow hover:text-black transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1600px] px-6 md:px-10 py-10">
        <div className="flex flex-wrap gap-3 mb-8">
          {["all", ...STATUS].map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`font-mono-accent text-xs uppercase tracking-[0.2em] px-4 py-3 border border-black/20 transition-colors ${
                filter === s ? "bg-black text-[#F5F4EF]" : "hover:bg-black/5"
              }`}
            >
              {s} {s !== "all" && <span className="opacity-60">({counts[s] || 0})</span>}
              {s === "all" && <span className="opacity-60">({items.length})</span>}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-3 py-20 justify-center text-black/50"><Loader2 className="animate-spin" /> Loading enquiries…</div>
        ) : shown.length === 0 ? (
          <div data-testid="admin-empty" className="border border-black/15 py-24 text-center text-black/50 font-mono-accent uppercase tracking-[0.2em] text-sm">No enquiries here</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {shown.map((e) => (
              <div key={e.id} data-testid={`enquiry-${e.id}`} className="border border-black/15 bg-white p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="font-display text-3xl tracking-tighter">{e.grade}</span>
                      <span className="font-mono-accent text-sm text-black/50">· {e.quantity}</span>
                    </div>
                    <p className="font-medium mt-1">{e.name}</p>
                  </div>
                  <span className={`font-mono-accent text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 ${statusColor[e.status] || "bg-black/10"}`}>{e.status}</span>
                </div>
                <div className="space-y-1.5 text-sm text-black/70 mb-5">
                  <a href={`tel:${e.phone}`} className="flex items-center gap-2 hover:text-primary"><Phone size={14} /> {e.phone}</a>
                  <p className="flex items-center gap-2"><MapPin size={14} /> {e.location}</p>
                  {e.email && <p className="pl-6">{e.email}</p>}
                  {e.required_date && <p className="pl-6">Required: {e.required_date}</p>}
                  {e.message && <p className="pl-6 italic">“{e.message}”</p>}
                  <p className="pl-6 text-black/40 text-xs">{new Date(e.created_at).toLocaleString()}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2 border-t border-black/10 pt-4">
                  {STATUS.map((s) => (
                    <button
                      key={s}
                      data-testid={`set-${s}-${e.id}`}
                      onClick={() => updateStatus(e.id, s)}
                      disabled={e.status === s}
                      className="font-mono-accent text-[10px] uppercase tracking-[0.15em] px-3 py-2 border border-black/20 hover:bg-black hover:text-[#F5F4EF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      {s}
                    </button>
                  ))}
                  <button data-testid={`delete-${e.id}`} onClick={() => remove(e.id)} className="ml-auto text-black/40 hover:text-primary transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
